export interface HubSpotTrackingParams {
    hutk?: string;
    page_uri?: string;
    page_name?: string;
    referrer?: string;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_term?: string;
    utm_content?: string;
}

const TRACKING_KEYS = [
    'hutk',
    'page_uri',
    'page_name',
    'referrer',
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
] as const;

function readCookie(name: string): string | null {
    if (typeof document === 'undefined') {
        return null;
    }

    const prefix = `${name}=`;
    const parts = document.cookie.split(';');
    for (const part of parts) {
        const trimmed = part.trim();
        if (trimmed.startsWith(prefix)) {
            return decodeURIComponent(trimmed.slice(prefix.length));
        }
    }
    return null;
}

function isInIframe(): boolean {
    try {
        return window.self !== window.top;
    } catch {
        return true;
    }
}

/**
 * Tracking fields the booking API forwards to HubSpot Forms API context.
 *
 * Cross-domain iframes must receive these on the iframe URL (see embed.js).
 * Same-origin / widget.js can also pick up hubspotutk from the current page.
 */
export function getHubSpotTrackingFromLocation(
    search: string = typeof window !== 'undefined' ? window.location.search : ''
): HubSpotTrackingParams | undefined {
    const params = new URLSearchParams(search);
    const tracking: HubSpotTrackingParams = {};

    for (const key of TRACKING_KEYS) {
        const value = params.get(key)?.trim();
        if (value) {
            tracking[key] = value;
        }
    }

    if (!tracking.hutk) {
        const hutk = readCookie('hubspotutk');
        if (hutk) {
            tracking.hutk = hutk;
        }
    }

    if (!tracking.page_uri && typeof window !== 'undefined') {
        if (isInIframe()) {
            if (document.referrer) {
                tracking.page_uri = document.referrer;
            }
        } else {
            tracking.page_uri = window.location.href;
        }
    }

    if (!tracking.page_name && typeof document !== 'undefined' && document.title && !isInIframe()) {
        tracking.page_name = document.title;
    }

    // In an iframe document.referrer is the parent page, which is already page_uri -
    // using it as the referrer would look like a self-referral. embed.js forwards
    // the parent's real referrer as a query param instead.
    if (!tracking.referrer && typeof document !== 'undefined' && document.referrer && !isInIframe()) {
        tracking.referrer = document.referrer;
    }

    return Object.keys(tracking).length > 0 ? tracking : undefined;
}

export function withHubSpotTracking<T extends object>(
    data: T
): T & { hubspot_tracking?: HubSpotTrackingParams } {
    const tracking = getHubSpotTrackingFromLocation();
    if (!tracking) {
        return data;
    }
    return { ...data, hubspot_tracking: tracking };
}
